import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ExternalLink, Mail, Phone, Linkedin } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Footer = () => {
  const quickLinks = [
    { name: "Über mich", href: "/#about" },
    { name: "Leistungen", href: "/#services" },
    { name: "Projekte", href: "/#projects" },
    { name: "Kontakt", href: "/#contact" }
  ];

  const projects = [
    { name: "Skill4Future", href: "https://skill4future.de" },
    { name: "Gründermatch", href: "https://gruendermatch.de" },
    { name: "Lexo.Tax", href: "https://lexo.tax" },
    { name: "Walbee", href: "https://walbee.de" }
  ];

  return (
    <footer className="bg-primary text-primary-foreground relative overflow-hidden">
      <div className="absolute inset-0 bg-dot-pattern opacity-5"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
        <div className="grid md:grid-cols-4 gap-8">
          <motion.div 
            className="md:col-span-1"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <a href="/#hero" className="inline-block mb-4" aria-label="Startseite">
              <img
                src="/lovable-uploads/577215ac-ab3f-4f5d-b337-701ad641df1c.png"
                alt="MA4Consult Logo"
                className="h-14 md:h-16 w-auto brightness-0 invert"
                loading="lazy"
              />
            </a>
            <p className="text-primary-foreground/80 mb-6 text-sm leading-relaxed">
              Beratung mit Klarheit, Struktur und Strategie für Ihren 
              unternehmerischen Erfolg.
            </p>
            <div className="flex gap-3">
              <motion.div whileHover={{ scale: 1.1 }} transition={{ duration: 0.2 }}>
                <Button variant="secondary" size="icon" asChild>
                  <a href="mailto:mk@ma4consult.de" aria-label="E-Mail an Maria Keutel senden">
                    <Mail className="w-4 h-4" />
                  </a>
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1 }} transition={{ duration: 0.2 }}>
                <Button variant="secondary" size="icon" asChild>
                  <a href="tel:+4917331594421" aria-label="Maria Keutel anrufen">
                    <Phone className="w-4 h-4" />
                  </a>
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1 }} transition={{ duration: 0.2 }}>
                <Button variant="secondary" size="icon" asChild>
                  <a 
                    href="https://www.linkedin.com/in/maria-keutel-090a46aa/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="LinkedIn-Profil von Maria Keutel"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                </Button>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="font-semibold mb-4">Navigation</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <motion.li 
                  key={index}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <a 
                    href={link.href}
                    className="text-primary-foreground/80 hover:text-primary-foreground text-sm transition-[var(--transition-smooth)]"
                  >
                    {link.name}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="font-semibold mb-4">Meine Projekte</h4>
            <ul className="space-y-2">
              {projects.map((project, index) => (
                <motion.li 
                  key={index}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <a 
                    href={project.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-foreground/80 hover:text-primary-foreground text-sm transition-[var(--transition-smooth)] flex items-center gap-1"
                  >
                    {project.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="font-semibold mb-4">Kontakt</h4>
            <div className="space-y-2 text-sm text-primary-foreground/80">
              <p>Maria Keutel</p>
              <p>Rosenheim, Bayern</p>
              <p>
                <a 
                  href="mailto:mk@ma4consult.de"
                  className="hover:text-primary-foreground transition-[var(--transition-smooth)]"
                >
                  mk@ma4consult.de
                </a>
              </p>
              <p>
                <a 
                  href="tel:+4917331594421"
                  className="hover:text-primary-foreground transition-[var(--transition-smooth)]"
                >
                  0173 31 59 421
                </a>
              </p>
            </div>
          </motion.div>
        </div>

        <Separator className="my-8 bg-primary-foreground/20" />

        <motion.div 
          className="flex flex-col md:flex-row justify-between items-center gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="text-sm text-primary-foreground/60">
            <p>&copy; {new Date().getFullYear()} MA4Consult - Maria Keutel. Alle Rechte vorbehalten.</p>
          </div>
          <div className="flex gap-6 text-sm text-primary-foreground/60">
            <Link to="/impressum" className="hover:text-primary-foreground transition-[var(--transition-smooth)]">
              Impressum
            </Link>
            <Link to="/datenschutz" className="hover:text-primary-foreground transition-[var(--transition-smooth)]">
              Datenschutz
            </Link>
            <a href="#" className="hover:text-primary-foreground transition-[var(--transition-smooth)]">
              AGB
            </a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
